/**
 * 导出任务列表面板
 */
import React from 'react';
import { fmtDateTimeCST } from '../utils/datetime';
import { ExportTaskInfo } from '../services/api';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  QueueListIcon,
  DocumentArrowDownIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

interface ContentEditTaskPanelProps {
  show: boolean;
  taskList: ExportTaskInfo[];
  isAdmin: boolean;
  currentUserId?: number;
  onClose: () => void;
  onDownload: (task: ExportTaskInfo) => void;
  onDelete: (taskId: number) => void;
}

const formatTaskTime = (isoString: string | null): string => fmtDateTimeCST(isoString);

const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const ContentEditTaskPanel: React.FC<ContentEditTaskPanelProps> = ({
  show,
  taskList,
  isAdmin,
  currentUserId,
  onClose,
  onDownload,
  onDelete,
}) => {
  if (!show) return null;

  const newCompleted = taskList.filter(t => t.status === 'completed' && !t.downloaded).length;
  const running = taskList.filter(t => t.status === 'running').length;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center pt-16">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[70vh] bg-white rounded-xl shadow-2xl flex flex-col mx-4">
        {/* 标题栏 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <QueueListIcon className="w-5 h-5 text-blue-600" />
            导出任务列表
            {(() => {
              const badges: React.ReactNode[] = [];
              if (newCompleted > 0) {
                badges.push(
                  <span key="completed" className="text-sm font-normal text-red-600 bg-red-50 px-2 py-0.5 rounded animate-pulse">
                    {newCompleted} 个任务已完成
                  </span>
                );
              }
              if (running > 0) {
                badges.push(
                  <span key="running" className="text-sm font-normal text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                    {running} 个任务进行中
                  </span>
                );
              }
              return <>{badges}</>;
            })()}
          </h3>
          <button onClick={onClose} className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* 任务列表 */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {taskList.length === 0 ? (
            <div className="text-center py-12">
              <DocumentArrowDownIcon className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-3 text-sm text-gray-500">暂无导出任务</p>
              <p className="mt-1 text-xs text-gray-400">点击"导出Word"创建新任务</p>
            </div>
          ) : (
            <div className="space-y-3">
              {taskList.map(task => (
                <div
                  key={task.task_id}
                  className={`border rounded-lg p-4 transition-colors ${
                    task.status === 'completed'
                      ? 'border-green-200 bg-green-50/30'
                      : task.status === 'failed'
                        ? 'border-red-200 bg-red-50/30'
                        : 'border-blue-200 bg-blue-50/30'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          #{task.task_id} {task.project_name}
                        </span>
                        <span className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          task.status === 'completed'
                            ? 'bg-green-100 text-green-700'
                            : task.status === 'failed'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-blue-100 text-blue-700'
                        }`}>
                          {task.status === 'completed' && <CheckCircleIcon className="w-3 h-3 mr-1" />}
                          {task.status === 'failed' && <ExclamationCircleIcon className="w-3 h-3 mr-1" />}
                          {task.status === 'running' && (
                            <svg className="animate-spin w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                          )}
                          {task.status === 'running' ? '生成中' : task.status === 'completed' ? '已完成' : '失败'}
                        </span>
                        {task.downloaded && task.status === 'completed' && (
                          <span className="flex-shrink-0 text-xs text-gray-400">已下载</span>
                        )}
                      </div>

                      {task.status === 'running' && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span>{task.current}</span>
                            <span>{task.completed}/{task.total}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-blue-600 h-1.5 rounded-full transition-all duration-500"
                              style={{ width: `${task.total > 0 ? (task.completed / task.total) * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {task.status === 'failed' && task.error_message && (
                        <details className="mt-1">
                          <summary className="text-xs text-red-600 cursor-pointer hover:text-red-500">
                            错误详情（点击展开）
                          </summary>
                          <pre className="mt-1 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
                            {task.error_message}
                          </pre>
                        </details>
                      )}

                      <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
                        {isAdmin && task.user_id != null && task.user_id !== currentUserId && (
                          <span className="text-purple-500 bg-purple-50 px-1.5 py-0.5 rounded" title="其他用户创建">
                            用户 #{task.user_id}
                          </span>
                        )}
                        <span>创建: {formatTaskTime(task.created_at)}</span>
                        {task.completed_at && (
                          <span className="text-green-500">完成: {formatTaskTime(task.completed_at)}</span>
                        )}
                        {task.failed_at && (
                          <span className="text-red-500">失败: {formatTaskTime(task.failed_at)}</span>
                        )}
                        {task.status === 'completed' && task.file_size > 0 && (
                          <span>文件大小: {formatFileSize(task.file_size)}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
                      {task.status === 'completed' && !task.downloaded && (
                        <button
                          onClick={() => onDownload(task)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-red-500 hover:bg-red-600 transition-colors animate-pulse"
                        >
                          <DocumentArrowDownIcon className="w-3.5 h-3.5 mr-1" />
                          下载
                        </button>
                      )}
                      {task.status === 'completed' && task.downloaded && (
                        <button
                          onClick={() => onDownload(task)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                        >
                          <DocumentArrowDownIcon className="w-3.5 h-3.5 mr-1" />
                          下载
                        </button>
                      )}
                      {(isAdmin || task.user_id === currentUserId) && (
                        <button
                          onClick={() => onDelete(task.task_id)}
                          className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="删除任务"
                        >
                          <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 底部操作 */}
        <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
          <span className="text-xs text-gray-400">共 {taskList.length} 个任务</span>
          <button
            onClick={() => {
              const toDelete = taskList.filter(t =>
                t.status !== 'running' &&
                (isAdmin || t.user_id === currentUserId)
              );
              Promise.all(toDelete.map(t => onDelete(t.task_id)));
            }}
            className="text-xs text-gray-500 hover:text-red-600 transition-colors"
          >
            清空已完成
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentEditTaskPanel;
